import { useEffect, useRef, useState } from 'react'

import UploadIcon from '@/Components/icons/upload.svg?react'
import { Button } from '@/Components/ui/button'

interface Props {
  onChangeFile: (file: File | null) => void
  previewUrl?: string
}

export const UploadArea = ({ onChangeFile, previewUrl }: Props) => {
  const [returnForm, setReturnForm] = useState<File | null>(null)

  const [isDragOver, setIsDragOver] = useState(false)
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [removeClicked, setRemoveClicked] = useState(false)
  // Determine if preview URL is PDF or image
  const isPreviewPdf = previewUrl?.toLowerCase().endsWith('.pdf') ?? false
  const isPreviewImage = previewUrl && !isPreviewPdf

  // Cleanup file preview URL on unmount
  useEffect(() => {
    return () => {
      if (filePreview && filePreview.startsWith('blob:')) {
        URL.revokeObjectURL(filePreview)
      }
    }
  }, [filePreview])

  // Handle PDF preview from URL
  useEffect(() => {
    if (previewUrl && isPreviewPdf && !returnForm) {
      setTimeout(() => {
        const adobeDiv = document.getElementById('adobe-pdf-view')
        if (adobeDiv) {
          adobeDiv.innerHTML = ''
          // @ts-expect-error - Adobe DC View is loaded globally
          const adobeDCView = new AdobeDC.View({
            clientId: '3daf1a0430a045879e5bf8163cad53c3',
            divId: 'adobe-pdf-view',
          })
          adobeDCView.previewFile(
            {
              content: { location: { url: previewUrl } },
              metaData: { fileName: 'document.pdf' },
            },
            { embedMode: 'IN_LINE' }
          )
        }
      }, 100)
    }
  }, [previewUrl, isPreviewPdf, returnForm])
  // File validation
  const validateFile = (file: File): boolean => {
    const allowedFileTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
    if (!allowedFileTypes.includes(file.type)) {
      setErrorMessage('ניתן להעלות תווית כתמונה או כPDF בלבד.')
      return false
    }
    setErrorMessage('')
    return true
  }

  // Handle file selection and preview
  const handleFileSelect = (file: File) => {
    if (!validateFile(file)) return
    setRemoveClicked(false)

    // Clear any existing PDF viewer
    const adobeDiv = document.getElementById('adobe-pdf-view')
    if (adobeDiv) {
      adobeDiv.innerHTML = ''
    }

    setReturnForm(file)
    onChangeFile(file)
    // Create preview for images
    if (file.type.startsWith('image/')) {
      setFilePreview(URL.createObjectURL(file))
    } else if (file.type === 'application/pdf') {
      setFilePreview(null)

      // Adobe PDF Embed API
      setTimeout(() => {
        const adobeDiv = document.getElementById('adobe-pdf-view')
        if (adobeDiv) {
          // @ts-expect-error - Adobe DC View is loaded globally
          const adobeDCView = new AdobeDC.View({
            clientId: '3daf1a0430a045879e5bf8163cad53c3',
            divId: 'adobe-pdf-view',
          })
          const reader = new FileReader()
          reader.onloadend = function (e) {
            const filePromise = Promise.resolve(e.target?.result)
            adobeDCView.previewFile(
              {
                content: { promise: filePromise },
                metaData: { fileName: file.name },
              },
              { embedMode: 'IN_LINE' }
            )
          }
          reader.readAsArrayBuffer(file)
        }
      }, 100)
    }
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  // Click to select file
  const handleClick = () => {
    fileInputRef.current?.click()
  }
  const showPdfView = returnForm?.type === 'application/pdf'
  const showPreview = returnForm || previewUrl

  // Get display file name
  const displayName = returnForm?.name

  const handleRemove = () => {
    setReturnForm(null)
    setFilePreview(null)
    setErrorMessage('')
    onChangeFile(null) // Clear the file in parent
    setRemoveClicked(true)
  }

  return (
    <div className='space-y-4'>
      {/* Upload Section - Only show when no file is selected and no preview URL */}
      {(removeClicked || !showPreview) && (
        <div
          className={`border-border-default flex h-[200px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed bg-white p-8 text-center transition-colors ${
            isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        >
          <UploadIcon className={`h-6 w-6 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
          <p className='text-text-secondary text-sm'>גרור לכאן קובץ או לחץ לבחירה</p>
          <p className='text-text-placeholder text-xs'>תמונה או PDF בלבד</p>
          <input ref={fileInputRef} type='file' accept='image/*,.pdf' onChange={handleFileUpload} className='hidden' />
        </div>
      )}

      {/* File Preview Section - Show when file is selected or previewUrl is provided */}
      {showPreview && !removeClicked && (
        <div className='rounded-lg bg-white p-4 shadow-sm'>
          <div className='mb-3 flex items-center justify-between'>
            <div className='grow'>
              {displayName && <p className='line-clamp-2 max-w-[80%] text-sm font-medium'>קובץ נבחר: {displayName}</p>}
              {returnForm && <p className='text-xs'>גודל: {(returnForm.size / 1024 / 1024).toFixed(2)} MB</p>}
            </div>
            <Button variant='outline' size='sm' onClick={handleRemove}>
              הסר
            </Button>
          </div>

          {/* Image Preview - Show for new file or preview URL */}
          {(filePreview || isPreviewImage) && (
            <div className='flex justify-center'>
              <img src={filePreview || previewUrl || ''} alt='File preview' className='max-h-64 w-auto rounded border' />
            </div>
          )}

          {/* PDF Preview - Show for new file or preview URL PDF */}
          {(showPdfView || (previewUrl && isPreviewPdf)) && (
            <div className='w-full'>
              <div id='adobe-pdf-view' className='h-96 w-full rounded'></div>
            </div>
          )}
          {errorMessage && (
            <div className='rounded-lg border border-red-200 bg-red-50 p-3'>
              <p className='text-sm text-red-800'>{errorMessage}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
